import API_BASE from '../../config';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCardIcon } from '@heroicons/react/24/outline';
import { useTour } from '../../tour/TourContext';
import { sampleCreditBalance } from '../../tour/sampleData';

const Navbar: React.FC = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [creditBalance, setCreditBalance] = useState<any>(null);
  const navigate = useNavigate();
  const { isSampleMode } = useTour();

  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        setUser(JSON.parse(userStr));
      }
    } catch {}
  }, []);

  // Fetch credit balance for owners
  useEffect(() => {
    const fetchCreditBalance = async () => {
      if (user?.role === 'OWNER') {
        if (isSampleMode) {
          setCreditBalance(sampleCreditBalance.data);
          return;
        }
        try {
          const response = await fetch(API_BASE + '/api/credits/balance', {
            credentials: 'include',
          });
          const data = await response.json();
          if (data.success) {
            setCreditBalance(data.data);
          }
        } catch (error) {
          console.error('Error fetching credit balance:', error);
        }
      }
    };

    fetchCreditBalance();
  }, [user, isSampleMode]);

  const handleLogout = async () => {
    await fetch(API_BASE + '/api/logout', {
      method: 'POST',
      credentials: 'include',
    });
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <nav className="bg-[#FFFFFF] border border-[#E8EDF5] shadow-sm mx-4 mt-3 mb-2 rounded-card">
      <div className="max-w-7xl mx-auto px-3 sm:px-4">
        <div className="flex justify-end h-11">
          <div className="flex items-center gap-3 pr-1">
            {user?.role === 'OWNER' && creditBalance && (
              <button
                type="button"
                data-tour="navbar-credits"
                onClick={() => navigate('/settings?tab=billing')}
                className="hidden sm:flex items-center gap-1.5 rounded-xl border border-[#E8EDF5] bg-[#F7F8FA] px-2.5 py-1 text-xs font-semibold text-[#0065F4] hover:bg-[#EAF1FF]"
              >
                <CreditCardIcon className="w-3.5 h-3.5" />
                <span>{creditBalance.isFreeTier ? 'FREE' : creditBalance.balance}</span>
              </button>
            )}
            <div className="relative group">
              <button
                className="flex items-center space-x-2 text-gray-700 hover:text-[#0065F4]"
              >
                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-[10px] font-medium">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </span>
                </div>
                <span className="text-xs">{user?.firstName} {user?.lastName}</span>
              </button>

              <div className="absolute right-0 mt-2 w-48 bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl shadow-sm py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                {/* Credit Balance for Owners */}
                {user?.role === 'OWNER' && creditBalance && (
                  <div className="px-4 py-3 border-b border-gray-200/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <CreditCardIcon className="w-4 h-4 text-[#0065F4] mr-2" />
                        <span className="text-sm font-medium text-gray-700">Credits</span>
                      </div>
                      <span className="text-sm font-bold text-[#0065F4]">
                        {creditBalance.isFreeTier ? 'FREE' : creditBalance.balance}
                      </span>
                    </div>
                    {creditBalance.isFreeTier && (
                      <div className="text-xs text-green-600 mt-1">
                        Free tier active
                      </div>
                    )}
                    {!creditBalance.isFreeTier && creditBalance.daysRemaining < 7 && (
                      <div className="text-xs text-red-600 mt-1">
                        {creditBalance.daysRemaining} days left
                      </div>
                    )}
                  </div>
                )}
                
                <button
                  onClick={() => navigate('/settings')}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#F1F5FF] hover:text-[#0065F4] w-full text-left"
                >
                  Settings
                </button>
                <button
                  onClick={handleLogout}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#F1F5FF] hover:text-[#0065F4] w-full text-left"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 